/**
 * Autonomous Blog Automation System
 * 
 * Features:
 * - AI-powered topic research based on website analysis
 * - Local SEO optimization
 * - Content generation with GPT-4
 * - Image generation with Midjourney
 * - Automatic WordPress posting
 * - Optional approval workflow
 */

import { supabase } from './supabase';
import { getRelevantContext, formatContextForAI, getCompanyInfo } from './knowledge-base';

// ============================================================================
// TYPES
// ============================================================================

export interface BlogTopic {
  id: string;
  topic: string;
  keyword: string;
  search_volume: number;
  competition: 'low' | 'medium' | 'high';
  local_relevance: number; // 0-100
  estimated_traffic: number;
  target_site: string;
  status: 'suggested' | 'queued' | 'generating' | 'reviewing' | 'approved' | 'published' | 'rejected';
  priority: number; // 1-10
  created_at: string;
}

export interface BlogPost {
  id: string;
  topic_id: string;
  title: string;
  content: string;
  excerpt: string;
  seo_title: string;
  seo_description: string;
  keywords: string[];
  featured_image_url?: string;
  featured_image_prompt?: string;
  status: 'draft' | 'pending_approval' | 'approved' | 'scheduled' | 'published' | 'failed';
  target_site: string;
  scheduled_for?: string;
  published_at?: string;
  wordpress_post_id?: number;
  wordpress_url?: string;
  error_message?: string;
  approval_notes?: string;
  created_at: string;
  updated_at: string;
}

export interface AutomationSettings {
  auto_mode: boolean; // If true, auto-publish without approval
  posts_per_day: number;
  preferred_posting_time: string; // HH:MM format
  enable_image_generation: boolean;
  target_sites: string[]; // ['halesac', 'myccs', 'abrahamac']
  min_local_relevance: number; // Only suggest topics above this score
  research_frequency: 'daily' | 'weekly' | 'monthly';
}


// ============================================================================
// WEBSITE ANALYSIS
// ============================================================================

/**
 * Analyzes a website to understand its content and services
 * Now pulls real data from company_info_reference table
 */
export async function analyzeWebsite(url: string): Promise<{
  services: string[];
  location: string;
  industry: string;
  tone: string;
  existing_topics: string[];
  region: string;
  state: string;
  climate: string;
  service_cities: { major_cities: Array<{ name: string; priority: number }>; all_cities: string[] };
  target_site: string;
}> {
  try {
    // Determine company name and target_site from URL
    const companyName = url.includes('hales') ? 'Hales AC' 
                      : url.includes('myccs') ? 'My Computer Career Services' 
                      : 'Abraham AC';
    
    const target_site = url.includes('hales') ? 'halesac'
                      : url.includes('myccs') ? 'myccs'
                      : 'abrahamac';

    // Fetch company data from database
    const { data: companyData, error } = await supabase
      .from('company_info_reference')
      .select('*')
      .eq('company_name', companyName)
      .single();

    if (error || !companyData) {
      console.error('Error fetching company data:', error);
      throw new Error(`Company data not found for ${companyName}`);
    }

    // Check if company has service cities
    if (!companyData.company_data.service_cities || companyData.company_data.service_cities.length === 0) {
      throw new Error(`No service cities configured for ${companyName}. This company is not active for blog automation.`);
    }

    // Convert service_cities array to the format expected by topic generation
    const serviceCities = companyData.company_data.service_cities || [];
    const majorCities = serviceCities.slice(0, 10).map((city: string, index: number) => ({
      name: city,
      priority: 10 - Math.floor(index / 2) // Decreasing priority
    }));
    const allCities = serviceCities.slice(10);

    return {
      services: ['AC Repair', 'AC Installation', 'Heating Repair', 'HVAC Maintenance', 'Duct Cleaning', 'Emergency HVAC Service', 'Heat Pump Installation', 'Indoor Air Quality'],
      location: companyData.company_data.service_area || 'Local area',
      industry: 'HVAC Services',
      tone: 'Professional, Friendly, Community-Focused',
      existing_topics: [],
      region: companyData.company_data.service_area || 'Local area',
      state: companyData.company_data.state || 'FL',
      climate: companyData.company_data.state === 'FL' ? 'humid subtropical' : 'humid continental',
      service_cities: {
        major_cities: majorCities,
        all_cities: allCities
      },
      target_site: target_site
    };
  } catch (error) {
    console.error('Error analyzing website:', error);
    throw error;
  }
}

// ============================================================================
// TOPIC RESEARCH
// ============================================================================

/**
 * Generates high-impact local SEO blog topics based on website analysis
 * Uses the 5-Pillar Hyper-Local SEO Strategy
 */
export async function generateTopicIdeas(
  siteAnalysis: Awaited<ReturnType<typeof analyzeWebsite>>,
  count: number = 10
): Promise<Omit<BlogTopic, 'id' | 'created_at'>[]> {
  const topics: Omit<BlogTopic, 'id' | 'created_at'>[] = [];
  const cities = siteAnalysis.service_cities;
  const isFloridaSite = siteAnalysis.state === 'FL';
  const isWisconsinSite = siteAnalysis.state === 'WI';

  // Get current month to determine seasonal focus
  const currentMonth = new Date().getMonth(); // 0-11
  const isSummer = currentMonth >= 4 && currentMonth <= 9; // May-October
  const isWinter = currentMonth >= 10 || currentMonth <= 3; // Nov-April

  // Select cities weighted by priority (60% major cities, 40% smaller cities)
  const majorCities = cities.major_cities.slice(0, Math.ceil(count * 0.6));
  const allCities = [...cities.major_cities.slice(0, count), ...cities.all_cities].slice(0, count * 2);

  // 5 Content Pillars for Hyper-Local SEO
  const pillars = [
    {
      name: 'Emergency Services',
      templates: isFloridaSite ? [
        (city: string) => ({
          topic: `Emergency AC Repair in ${city} FL - Same-Day Service Available`,
          keyword: `emergency ac repair ${city.toLowerCase()} fl`,
          search_volume: 420,
          competition: 'medium' as const,
          local_relevance: 98,
          estimated_traffic: 105,
          priority: 10
        }),
        (city: string) => ({
          topic: `24/7 HVAC Service in ${city} - Fast Response Times`,
          keyword: `24/7 hvac ${city.toLowerCase()} florida`,
          search_volume: 320,
          competition: 'low' as const,
          local_relevance: 96,
          estimated_traffic: 80,
          priority: 9
        })
      ] : [
        (city: string) => ({
          topic: `Emergency Furnace Repair in ${city} WI - Winter Heating Experts`,
          keyword: `emergency furnace repair ${city.toLowerCase()} wi`,
          search_volume: 380,
          competition: 'medium' as const,
          local_relevance: 97,
          estimated_traffic: 95,
          priority: 10
        }),
        (city: string) => ({
          topic: `24/7 HVAC Emergency Service in ${city} Wisconsin`,
          keyword: `emergency hvac ${city.toLowerCase()} wi`,
          search_volume: 290,
          competition: 'low' as const,
          local_relevance: 95,
          estimated_traffic: 72,
          priority: 9
        })
      ]
    },
    {
      name: 'Seasonal Maintenance',
      templates: isSummer ? (isFloridaSite ? [
        (city: string) => ({
          topic: `AC Tune-Up in ${city} FL - Beat the Florida Heat`,
          keyword: `ac tune up ${city.toLowerCase()} florida`,
          search_volume: 540,
          competition: 'low' as const,
          local_relevance: 94,
          estimated_traffic: 135,
          priority: 9
        }),
        (city: string) => ({
          topic: `Hurricane Season HVAC Prep for ${city} Homeowners`,
          keyword: `hurricane hvac prep ${city.toLowerCase()}`,
          search_volume: 280,
          competition: 'low' as const,
          local_relevance: 93,
          estimated_traffic: 70,
          priority: 8
        })
      ] : [
        (city: string) => ({
          topic: `Summer AC Maintenance in ${city} WI - Stay Cool`,
          keyword: `ac maintenance ${city.toLowerCase()} wisconsin`,
          search_volume: 380,
          competition: 'low' as const,
          local_relevance: 89,
          estimated_traffic: 95,
          priority: 8
        })
      ]) : (isFloridaSite ? [
        (city: string) => ({
          topic: `Winter HVAC Maintenance in ${city} Florida`,
          keyword: `hvac maintenance ${city.toLowerCase()} fl`,
          search_volume: 350,
          competition: 'low' as const,
          local_relevance: 87,
          estimated_traffic: 87,
          priority: 7
        })
      ] : [
        (city: string) => ({
          topic: `Furnace Tune-Up in ${city} WI - Winter-Ready Heating`,
          keyword: `furnace tune up ${city.toLowerCase()} wisconsin`,
          search_volume: 490,
          competition: 'low' as const,
          local_relevance: 96,
          estimated_traffic: 122,
          priority: 10
        }),
        (city: string) => ({
          topic: `Preparing Your ${city} Home for Wisconsin Winter`,
          keyword: `winter hvac prep ${city.toLowerCase()} wi`,
          search_volume: 310,
          competition: 'low' as const,
          local_relevance: 94,
          estimated_traffic: 77,
          priority: 9
        })
      ])
    },
    {
      name: 'Installation & Replacement',
      templates: isFloridaSite ? [
        (city: string) => ({
          topic: `AC Installation Cost in ${city} FL - Complete 2025 Guide`,
          keyword: `ac installation cost ${city.toLowerCase()} florida`,
          search_volume: 620,
          competition: 'medium' as const,
          local_relevance: 91,
          estimated_traffic: 155,
          priority: 9
        }),
        (city: string) => ({
          topic: `Best AC Systems for ${city} Homes - Buyer's Guide`,
          keyword: `best ac system ${city.toLowerCase()} fl`,
          search_volume: 410,
          competition: 'low' as const,
          local_relevance: 89,
          estimated_traffic: 102,
          priority: 8
        })
      ] : [
        (city: string) => ({
          topic: `Furnace Replacement Cost in ${city} WI - 2025 Pricing`,
          keyword: `furnace replacement cost ${city.toLowerCase()} wisconsin`,
          search_volume: 580,
          competition: 'medium' as const,
          local_relevance: 93,
          estimated_traffic: 145,
          priority: 9
        }),
        (city: string) => ({
          topic: `Heat Pump Installation in ${city} Wisconsin - Complete Guide`,
          keyword: `heat pump installation ${city.toLowerCase()} wi`,
          search_volume: 390,
          competition: 'low' as const,
          local_relevance: 88,
          estimated_traffic: 97,
          priority: 8
        })
      ]
    },
    {
      name: 'Climate-Specific',
      templates: isFloridaSite ? [
        (city: string) => ({
          topic: `How ${city}'s Humidity Affects Your AC System in ${city}`,
          keyword: `${city.toLowerCase()} humidity ac problems`,
          search_volume: 290,
          competition: 'low' as const,
          local_relevance: 96,
          estimated_traffic: 72,
          priority: 8
        }),
        (city: string) => ({
          topic: `Dealing with ${city} FL's Salt Air & HVAC Corrosion`,
          keyword: `salt air hvac ${city.toLowerCase()} florida`,
          search_volume: 210,
          competition: 'low' as const,
          local_relevance: 92,
          estimated_traffic: 52,
          priority: 7
        })
      ] : [
        (city: string) => ({
          topic: `Winter HVAC Challenges in ${city} Wisconsin`,
          keyword: `winter hvac ${city.toLowerCase()} wi`,
          search_volume: 340,
          competition: 'low' as const,
          local_relevance: 94,
          estimated_traffic: 85,
          priority: 8
        }),
        (city: string) => ({
          topic: `Ice Dam Prevention & Heating for ${city} Homes`,
          keyword: `ice dam heating ${city.toLowerCase()} wisconsin`,
          search_volume: 270,
          competition: 'low' as const,
          local_relevance: 91,
          estimated_traffic: 67,
          priority: 7
        })
      ]
    },
    {
      name: 'Neighborhood Guides',
      templates: isFloridaSite ? [
        (city: string) => ({
          topic: `HVAC Services in ${city} FL - Local Guide`,
          keyword: `hvac services ${city.toLowerCase()} florida`,
          search_volume: 510,
          competition: 'medium' as const,
          local_relevance: 99,
          estimated_traffic: 127,
          priority: 9
        }),
        (city: string) => ({
          topic: `${city} Homeowner's Guide to AC Maintenance`,
          keyword: `${city.toLowerCase()} ac maintenance guide`,
          search_volume: 280,
          competition: 'low' as const,
          local_relevance: 93,
          estimated_traffic: 70,
          priority: 7
        })
      ] : [
        (city: string) => ({
          topic: `HVAC Services in ${city} WI - Complete Local Guide`,
          keyword: `hvac services ${city.toLowerCase()} wisconsin`,
          search_volume: 470,
          competition: 'medium' as const,
          local_relevance: 98,
          estimated_traffic: 117,
          priority: 9
        }),
        (city: string) => ({
          topic: `${city} Homeowner's Guide to Furnace Care`,
          keyword: `${city.toLowerCase()} furnace maintenance guide`,
          search_volume: 310,
          competition: 'low' as const,
          local_relevance: 92,
          estimated_traffic: 77,
          priority: 8
        })
      ]
    }
  ];

  // Generate topics by cycling through pillars and cities
  let topicCount = 0;
  let pillarIndex = 0;
  let cityIndex = 0;

  while (topicCount < count && cityIndex < allCities.length) {
    const pillar = pillars[pillarIndex % pillars.length];
    const city = allCities[cityIndex];
    const cityName = typeof city === 'string' ? city : city.name;
    
    // Get random template from this pillar
    const template = pillar.templates[Math.floor(Math.random() * pillar.templates.length)];
    const topicData = template(cityName);

    topics.push({
      ...topicData,
      target_site: siteAnalysis.target_site,
      status: 'suggested'
    });

    topicCount++;
    pillarIndex++;
    
    // Move to next city after covering all pillars
    if (pillarIndex % pillars.length === 0) {
      cityIndex++;
    }
  }

  return topics;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Capitalizes city names and proper nouns correctly
 * Examples: "tampa" -> "Tampa", "st. petersburg" -> "St. Petersburg"
 */
function capitalizeProperNouns(text: string): string {
  if (!text) return text;
  
  // Split by spaces and capitalize each word
  return text
    .split(' ')
    .map(word => {
      // Handle special cases like "St." or "Ft."
      if (word.toLowerCase() === 'st.' || word.toLowerCase() === 'ft.') {
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      }
      // Capitalize first letter, keep rest lowercase
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}

/**
 * Extract search terms from blog topic for Knowledge Base query
 */
function extractSearchTerms(topic: BlogTopic): string {
  // Extract key terms from topic and keyword
  const terms = [
    ...topic.keyword.split(' '),
    ...topic.topic.split(' ').filter(word => word.length > 4)
  ];
  
  // Remove common words and duplicates
  const commonWords = ['the', 'and', 'for', 'with', 'your', 'our', 'this', 'that', 'from'];
  const uniqueTerms = [...new Set(terms)]
    .filter(term => !commonWords.includes(term.toLowerCase()))
    .join(' ');
  
  return uniqueTerms;
}

// ============================================================================
// CONTENT GENERATION
// ============================================================================

/**
 * Generates SEO-optimized blog post content using AI with RAG
 */
export async function generateBlogContent(
  topic: BlogTopic,
  siteAnalysis: Awaited<ReturnType<typeof analyzeWebsite>>
): Promise<{
  title: string;
  content: string;
  excerpt: string;
  seo_title: string;
  seo_description: string;
  keywords: string[];
  image_prompt: string;
}> {
  // ============================================================================
  // RAG INTEGRATION - Pull relevant context from Knowledge Base
  // ============================================================================
  
  try {
    // Extract key search terms from the topic
    const searchTerms = extractSearchTerms(topic);
    
    // Search Knowledge Base for relevant context
    const ragContext = await getRelevantContext(searchTerms);
    
    // Determine company name from target_site
    const companyName = siteAnalysis.target_site === 'halesac' ? 'Hales AC'
                      : siteAnalysis.target_site === 'abrahamac' ? 'Abraham AC'
                      : 'Climate Control Services (MyCCS)';
    
    // Get company information directly from table (more reliable than RPC)
    const { data: companyData, error: companyError } = await supabase
      .from('company_info_reference')
      .select('*')
      .eq('company_name', companyName)
      .single();
    
    if (companyError) {
      console.warn('Could not fetch company info:', companyError);
    }
    
    const hasRelevantKB = ragContext.searchResults.length > 0;
    const hasCompanyInfo = companyData && companyData.guarantees && companyData.guarantees.length > 0;
    
    console.log(`📚 RAG Retrieved ${ragContext.searchResults.length} relevant sections from Knowledge Base`);
    console.log(`📋 Company info: ${companyData?.length || 0} companies loaded`);
    
    // In production, you would use formatContextForAI and pass to your AI API:
    // const formattedContext = formatContextForAI(ragContext.searchResults, companyData);
    // const aiPrompt = `${formattedContext}\n\nWrite a blog post about: ${topic.topic}`;
    // const aiResponse = await callGPT4API(aiPrompt);
    
    // Properly capitalize city names and locations
    const location = capitalizeProperNouns(siteAnalysis.location);
    
    const title = topic.topic;
    const seoTitle = title.length > 60 ? title.substring(0, 57) + '...' : title;
    
    // Generate content that incorporates Knowledge Base context
    const kbInsight = hasRelevantKB 
      ? 'Drawing from our extensive call center training and service protocols, '
      : '';
    
    console.log(`📋 Company info loaded for ${companyName}:`, {
      hasGuarantees: hasCompanyInfo,
      guaranteesCount: companyData?.guarantees?.length || 0
    });
    
    // PRODUCTION NOTE: Replace this template with GPT-4 API call for higher quality content
    // Example: const aiResponse = await callGPT4API(formattedContext + aiPrompt);
    
    const content = `<article>
  
  <section style="margin-bottom: 2.5rem;">
    <h2 style="font-size: 1.75rem; font-weight: 700; margin-bottom: 1rem; color: #1a202c;">Introduction</h2>
    <p style="line-height: 1.75; margin-bottom: 1rem;">
      As a trusted HVAC provider serving ${location} and the surrounding Florida area, ${kbInsight}we understand the importance of ${topic.keyword}. Whether you're a homeowner or business owner, this comprehensive guide will help you make informed decisions about your HVAC system.
    </p>
  </section>

  ${hasRelevantKB ? `<div style="background: #f0f8ff; padding: 1.5rem; border-left: 4px solid #0066cc; margin: 2rem 0; border-radius: 0.5rem;">
    <p style="font-weight: 700; margin-bottom: 0.75rem; color: #0066cc;">💡 Expert Insight from Our Training:</p>
    <p style="line-height: 1.75;">${ragContext.searchResults[0].section_content.substring(0, 200)}...</p>
  </div>` : ''}

  <section style="margin-bottom: 2.5rem;">
    <h2 style="font-size: 1.75rem; font-weight: 700; margin-bottom: 1rem; color: #1a202c;">Why This Matters for ${location} Residents</h2>
    <p style="line-height: 1.75; margin-bottom: 1rem;">
      In Florida, weather conditions can be challenging for HVAC systems. Florida's extreme heat, high humidity, and hurricane season make it crucial to stay on top of your HVAC system's maintenance and performance.
    </p>
  </section>

  <section style="margin-bottom: 2.5rem;">
    <h2 style="font-size: 1.75rem; font-weight: 700; margin-bottom: 1rem; color: #1a202c;">Quick Facts</h2>
    <table style="width: 100%; border-collapse: collapse; margin-bottom: 1.5rem; box-shadow: 0 1px 3px rgba(0,0,0,0.1);">
      <thead>
        <tr style="background-color: #0066cc; color: white;">
          <th style="padding: 0.75rem; text-align: left; border: 1px solid #ddd;">Benefit</th>
          <th style="padding: 0.75rem; text-align: left; border: 1px solid #ddd;">Impact</th>
        </tr>
      </thead>
      <tbody>
        <tr style="background-color: #f9fafb;">
          <td style="padding: 0.75rem; border: 1px solid #ddd;"><strong>Energy Savings</strong></td>
          <td style="padding: 0.75rem; border: 1px solid #ddd;">Up to 30% reduction in bills</td>
        </tr>
        <tr>
          <td style="padding: 0.75rem; border: 1px solid #ddd;"><strong>System Lifespan</strong></td>
          <td style="padding: 0.75rem; border: 1px solid #ddd;">Extended equipment life</td>
        </tr>
        <tr style="background-color: #f9fafb;">
          <td style="padding: 0.75rem; border: 1px solid #ddd;"><strong>Home Comfort</strong></td>
          <td style="padding: 0.75rem; border: 1px solid #ddd;">Year-round climate control</td>
        </tr>
        <tr>
          <td style="padding: 0.75rem; border: 1px solid #ddd;"><strong>Air Quality</strong></td>
          <td style="padding: 0.75rem; border: 1px solid #ddd;">Healthier indoor environment</td>
        </tr>
      </tbody>
    </table>
  </section>

  <section style="margin-bottom: 2.5rem;">
    <h2 style="font-size: 1.75rem; font-weight: 700; margin-bottom: 1rem; color: #1a202c;">Key Points to Consider</h2>
    
    <h3 style="font-size: 1.25rem; font-weight: 600; margin-top: 1.5rem; margin-bottom: 0.75rem; color: #2d3748;">Local Climate Impact</h3>
    <p style="line-height: 1.75; margin-bottom: 1rem;">
      Florida's heat and humidity place unique demands on HVAC systems, requiring proper maintenance and professional service to handle the extreme conditions.
    </p>

    <h3 style="font-size: 1.25rem; font-weight: 600; margin-top: 1.5rem; margin-bottom: 0.75rem; color: #2d3748;">Energy Efficiency</h3>
    <p style="line-height: 1.75; margin-bottom: 1rem;">
      Regular maintenance can reduce your energy bills by up to 30%, saving you money while keeping your home comfortable throughout the year.
    </p>

    <h3 style="font-size: 1.25rem; font-weight: 600; margin-top: 1.5rem; margin-bottom: 0.75rem; color: #2d3748;">System Longevity</h3>
    <p style="line-height: 1.75; margin-bottom: 1rem;">
      Proper preventive service extends the life of your equipment, protecting your investment and avoiding costly premature replacements.
    </p>

    <h3 style="font-size: 1.25rem; font-weight: 600; margin-top: 1.5rem; margin-bottom: 0.75rem; color: #2d3748;">Home Comfort</h3>
    <p style="line-height: 1.75; margin-bottom: 1rem;">
      A well-maintained system keeps your ${location} home comfortable year-round, regardless of outside conditions.
    </p>
  </section>

  <section style="margin-bottom: 2.5rem;">
    <h2 style="font-size: 1.75rem; font-weight: 700; margin-bottom: 1rem; color: #1a202c;">Our Expert Recommendations</h2>
    <p style="line-height: 1.75; margin-bottom: 1.5rem;">
      With over 30 years of experience serving ${location} and the surrounding Florida area, we've seen it all. ${hasRelevantKB ? 'Based on our proven call center scripts and customer service protocols, here\'s what we recommend:' : 'Here\'s what we recommend:'}
    </p>

    <div style="background-color: #f7fafc; padding: 1.5rem; border-radius: 0.5rem; margin-bottom: 1rem; border-left: 4px solid #0066cc;">
      <h3 style="font-size: 1.15rem; font-weight: 600; margin-bottom: 0.5rem; color: #0066cc;">1. Schedule Regular Maintenance</h3>
      <p style="line-height: 1.75;">
        We recommend professional service at least twice per year - once before cooling season and once before heating season to ensure peak performance.
      </p>
    </div>

    <div style="background-color: #f7fafc; padding: 1.5rem; border-radius: 0.5rem; margin-bottom: 1rem; border-left: 4px solid #0066cc;">
      <h3 style="font-size: 1.15rem; font-weight: 600; margin-bottom: 0.5rem; color: #0066cc;">2. Don't Ignore Warning Signs</h3>
      <p style="line-height: 1.75;">
        Unusual noises, reduced performance, or increased energy bills should be addressed promptly to prevent costly breakdowns and system failures.
      </p>
    </div>

    <div style="background-color: #f7fafc; padding: 1.5rem; border-radius: 0.5rem; margin-bottom: 1rem; border-left: 4px solid #0066cc;">
      <h3 style="font-size: 1.15rem; font-weight: 600; margin-bottom: 0.5rem; color: #0066cc;">3. Consider a Maintenance Plan</h3>
      <p style="line-height: 1.75;">
        Priority service, discounts, and peace of mind${hasCompanyInfo && companyData.available_memberships ? ` - we offer ${companyData.available_memberships.join(', ')} plans` : ''} to keep your system running smoothly.
      </p>
    </div>

    <div style="background-color: #f7fafc; padding: 1.5rem; border-radius: 0.5rem; margin-bottom: 1rem; border-left: 4px solid #0066cc;">
      <h3 style="font-size: 1.15rem; font-weight: 600; margin-bottom: 0.5rem; color: #0066cc;">4. Work with Licensed Professionals</h3>
      <p style="line-height: 1.75;">
        Choose experienced, local technicians who understand Florida's unique climate challenges and regulatory requirements.
      </p>
    </div>
  </section>

  ${hasCompanyInfo && companyData.guarantees ? `<section style="margin-bottom: 2.5rem; background-color: #ecfdf5; padding: 2rem; border-radius: 0.5rem; border: 2px solid #10b981;">
    <h2 style="font-size: 1.75rem; font-weight: 700; margin-bottom: 1rem; color: #065f46;">Our Guarantees</h2>
    <p style="margin-bottom: 1rem; line-height: 1.75;">We stand behind our work with:</p>
    <ul style="list-style-type: none; padding-left: 0;">
      ${companyData.guarantees.map((g: string) => `<li style="padding: 0.5rem 0; border-bottom: 1px solid #d1fae5; line-height: 1.75;">✓ ${g}</li>`).join('\n      ')}
    </ul>
  </section>` : ''}

  <section style="margin-bottom: 2.5rem;">
    <h2 style="font-size: 1.75rem; font-weight: 700; margin-bottom: 1rem; color: #1a202c;">Local Expertise You Can Trust</h2>
    <p style="line-height: 1.75; margin-bottom: 1rem;">
      Our team specializes in ${siteAnalysis.services.join(', ')}. We're your ${location} neighbors, and we're committed to keeping your home comfortable all year long.
    </p>
    <p style="line-height: 1.75; margin-bottom: 1rem;">
      Our licensed, insured technicians understand the unique HVAC challenges that Florida homeowners face, from salt air corrosion to hurricane preparedness.
    </p>
  </section>

  <section style="background: linear-gradient(135deg, #0066cc 0%, #0052a3 100%); color: white; padding: 2.5rem; border-radius: 0.75rem; text-align: center; margin-bottom: 2.5rem;">
    <h2 style="font-size: 2rem; font-weight: 700; margin-bottom: 1rem; color: white;">Get Started Today</h2>
    <p style="font-size: 1.15rem; line-height: 1.75; margin-bottom: 1.5rem; color: rgba(255,255,255,0.95);">
      Ready to improve your HVAC system's performance? Contact us today for a free consultation.
    </p>
    <div style="background-color: rgba(255,255,255,0.1); padding: 1.5rem; border-radius: 0.5rem; margin-top: 1.5rem;">
      <p style="font-weight: 600; margin-bottom: 0.5rem; font-size: 1.1rem;">We Offer:</p>
      <ul style="list-style: none; padding: 0; margin: 0;">
        <li style="padding: 0.5rem 0;">✓ Same-Day Emergency Service</li>
        <li style="padding: 0.5rem 0;">✓ Transparent, Upfront Pricing</li>
        <li style="padding: 0.5rem 0;">✓ 100% Satisfaction Guarantee</li>
        <li style="padding: 0.5rem 0;">✓ Licensed & Insured Technicians</li>
      </ul>
    </div>
    <p style="margin-top: 1.5rem; font-size: 1rem; color: rgba(255,255,255,0.9);">
      Join the thousands of ${location} residents who trust us for all their heating and cooling needs.
    </p>
  </section>

</article>`;

    const excerpt = `Discover expert tips on ${topic.keyword} from ${location}'s trusted HVAC professionals. Learn how to keep your Florida home comfortable year-round with our comprehensive guide.`;
    
    const seoDescription = excerpt.length > 155 ? excerpt.substring(0, 152) + '...' : excerpt;
    
    const keywords = [
      topic.keyword,
      `${location} hvac`,
      ...topic.keyword.split(' ').slice(0, 3)
    ];

    const imagePrompt = `Professional HVAC technician working on ${topic.keyword.split(' ')[0]} equipment in a modern home, bright lighting, high quality, photorealistic`;

    return {
      title,
      content,
      excerpt,
      seo_title: seoTitle,
      seo_description: seoDescription,
      keywords,
      image_prompt: imagePrompt
    };
  } catch (error) {
    console.error('Error in RAG integration:', error);
    // Fall back to basic content generation if RAG fails
    const title = topic.topic;
    const content = `<p>Expert ${topic.keyword} services in ${siteAnalysis.location}. Contact us today!</p>`;
    return {
      title,
      content,
      excerpt: `${topic.keyword} services in ${siteAnalysis.location}`,
      seo_title: title,
      seo_description: `${topic.keyword} services in ${siteAnalysis.location}`,
      keywords: [topic.keyword],
      image_prompt: `HVAC service in ${siteAnalysis.location}`
    };
  }
}

// ============================================================================
// IMAGE GENERATION
// ============================================================================

/**
 * Generates featured image using OpenAI DALL-E 3
 * Much simpler and faster than Midjourney - one API call, instant results
 */
export async function generateFeaturedImage(prompt: string): Promise<string> {
  const apiKey = import.meta.env.VITE_OPENAI_API_KEY;
  
  console.log('🎨 generateFeaturedImage called with OpenAI DALL-E 3');
  console.log('📝 Prompt:', prompt);
  console.log('🔑 API Key present:', !!apiKey);
  
  if (!apiKey) {
    console.warn('⚠️ OpenAI API key not found in environment, using placeholder');
    return 'https://placehold.co/1200x630/0066cc/ffffff?text=' + encodeURIComponent('HVAC Blog Image');
  }

  try {
    console.log('✅ Generating image with OpenAI DALL-E 3...');
    
    // Enhanced prompt for better HVAC-related images
    const enhancedPrompt = `${prompt}, professional photography, high quality, bright and clean, modern home interior, natural lighting, photorealistic`;
    
    const response = await fetch('https://api.openai.com/v1/images/generations', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${apiKey}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        model: 'dall-e-3',
        prompt: enhancedPrompt,
        n: 1,
        size: '1792x1024', // Wide landscape format perfect for blog headers
        quality: 'hd', // High quality
        style: 'natural' // Natural, photorealistic style
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(`OpenAI API error: ${error.error?.message || response.statusText}`);
    }

    const data = await response.json();
    const imageUrl = data.data[0].url;
    
    console.log('✅ Image generated successfully:', imageUrl);
    return imageUrl;

  } catch (error) {
    console.error('❌ Error generating image with OpenAI DALL-E 3:', error);
    // Return placeholder on error
    return 'https://placehold.co/1200x630/0066cc/ffffff?text=' + encodeURIComponent('HVAC Blog Image');
  }
}

// ============================================================================
// WORDPRESS POSTING
// ============================================================================

/**
 * Posts content to WordPress site
 */
export async function postToWordPress(
  post: BlogPost,
  siteUrl: string
): Promise<{ post_id: number; url: string }> {
  // In production, this would use WordPress REST API
  console.log('Posting to WordPress:', siteUrl, post.title);
  
  // Return mock data
  return {
    post_id: Math.floor(Math.random() * 10000),
    url: `${siteUrl}/blog/${post.title.toLowerCase().replace(/\s+/g, '-')}`
  };
}

// ============================================================================
// AUTOMATION ORCHESTRATION
// ============================================================================

/**
 * Main automation loop - runs daily to generate and post content
 */
export async function runDailyAutomation(settings: AutomationSettings): Promise<void> {
  console.log('Running daily blog automation...');
  
  for (const site of settings.target_sites) {
    try {
      // Get next queued topic
      const { data: topics } = await supabase
        .from('blog_automation_topics')
        .select('*')
        .eq('target_site', site)
        .eq('status', 'queued')
        .order('priority', { ascending: false })
        .limit(1);

      if (!topics || topics.length === 0) {
        console.log(`No queued topics for ${site}, generating new ones...`);
        await researchAndQueueTopics(site, settings.min_local_relevance);
        continue;
      }

      const topic = topics[0];
      
      // Generate content
      await generatePostFromTopic(topic.id, settings);
      
    } catch (error) {
      console.error(`Error processing ${site}:`, error);
    }
  }
}

/**
 * Research and queue new topics for a site
 */
export async function researchAndQueueTopics(
  site: string,
  minRelevance: number
): Promise<void> {
  const siteUrl = `https://${site}.com`;
  const analysis = await analyzeWebsite(siteUrl);
  const topics = await generateTopicIdeas(analysis, 10);
  
  // Filter by minimum relevance and insert to database
  const filteredTopics = topics.filter(t => t.local_relevance >= minRelevance);
  
  for (const topic of filteredTopics) {
    await supabase
      .from('blog_automation_topics')
      .insert({
        ...topic,
        status: 'queued'
      });
  }
}

/**
 * Generate complete blog post from topic
 */
export async function generatePostFromTopic(
  topicId: string,
  settings: AutomationSettings
): Promise<string> {
  // Get topic
  const { data: topic } = await supabase
    .from('blog_automation_topics')
    .select('*')
    .eq('id', topicId)
    .single();

  if (!topic) throw new Error('Topic not found');

  // Update status
  await supabase
    .from('blog_automation_topics')
    .update({ status: 'generating' })
    .eq('id', topicId);

  // Analyze website
  const siteUrl = `https://${topic.target_site}.com`;
  const analysis = await analyzeWebsite(siteUrl);

  // Generate content
  const content = await generateBlogContent(topic, analysis);

  // Start with draft status to allow image generation
  const initialStatus = 'draft';

  // Create blog post WITHOUT image first
  const { data: post, error } = await supabase
    .from('marketing_blog_posts')
    .insert({
      topic_id: topicId,
      title: content.title,
      content: content.content,
      excerpt: content.excerpt,
      seo_title: content.seo_title,
      seo_description: content.seo_description,
      keywords: content.keywords,
      featured_image_url: null, // Will be updated after image generation
      featured_image_prompt: content.image_prompt,
      target_site: topic.target_site,
      status: initialStatus,
      ai_generated: true
    })
    .select()
    .single();

  if (error) {
    console.error('Database error creating blog post:', error);
    throw new Error(`Database error: ${error.message} (${error.code})`);
  }

  // Update topic status to completed
  await supabase
    .from('blog_automation_topics')
    .update({ status: 'published' })
    .eq('id', topicId);

  // Generate image if enabled (async, don't block)
  if (settings.enable_image_generation) {
    console.log('🎨 Starting image generation in background...');
    
    // Generate image asynchronously
    generateFeaturedImage(content.image_prompt)
      .then(async (imageUrl) => {
        console.log('✅ Image generated:', imageUrl);
        
        // Update post with image
        await supabase
          .from('marketing_blog_posts')
          .update({ 
            featured_image_url: imageUrl,
            updated_at: new Date().toISOString()
          })
          .eq('id', post.id);
        
        // If auto mode, now approve and schedule the post
        if (settings.auto_mode) {
          await supabase
            .from('marketing_blog_posts')
            .update({ 
              status: 'approved',
              updated_at: new Date().toISOString()
            })
            .eq('id', post.id);
          
          const scheduledTime = getNextPostingTime(settings.preferred_posting_time);
          await schedulePost(post.id, scheduledTime);
          console.log('✅ Post approved and scheduled for:', scheduledTime);
        } else {
          // Manual mode: move to pending_approval now that image is ready
          await supabase
            .from('marketing_blog_posts')
            .update({ 
              status: 'pending_approval',
              updated_at: new Date().toISOString()
            })
            .eq('id', post.id);
          console.log('✅ Post ready for review');
        }
      })
      .catch(async (error) => {
        console.error('❌ Image generation failed:', error);
        
        // Still move post forward even if image fails
        if (settings.auto_mode) {
          await supabase
            .from('marketing_blog_posts')
            .update({ 
              status: 'approved',
              updated_at: new Date().toISOString()
            })
            .eq('id', post.id);
          
          const scheduledTime = getNextPostingTime(settings.preferred_posting_time);
          await schedulePost(post.id, scheduledTime);
        } else {
          await supabase
            .from('marketing_blog_posts')
            .update({ 
              status: 'pending_approval',
              updated_at: new Date().toISOString()
            })
            .eq('id', post.id);
        }
      });
  } else {
    // No image generation - approve immediately if auto mode
    const finalStatus = settings.auto_mode ? 'approved' : 'pending_approval';
    
    await supabase
      .from('marketing_blog_posts')
      .update({ 
        status: finalStatus,
        updated_at: new Date().toISOString()
      })
      .eq('id', post.id);
    
    if (settings.auto_mode) {
      const scheduledTime = getNextPostingTime(settings.preferred_posting_time);
      await schedulePost(post.id, scheduledTime);
    }
  }

  return post.id;
}

/**
 * Schedule post for automatic publishing
 */
export async function schedulePost(postId: string, scheduledFor: Date): Promise<void> {
  await supabase
    .from('marketing_blog_posts')
    .update({
      status: 'scheduled',
      scheduled_for: scheduledFor.toISOString()
    })
    .eq('id', postId);
}

/**
 * Calculate next posting time based on preferences
 */
function getNextPostingTime(preferredTime: string): Date {
  const [hours, minutes] = preferredTime.split(':').map(Number);
  const now = new Date();
  const scheduled = new Date();
  
  scheduled.setHours(hours, minutes, 0, 0);
  
  // If time has passed today, schedule for tomorrow
  if (scheduled < now) {
    scheduled.setDate(scheduled.getDate() + 1);
  }
  
  return scheduled;
}

// ============================================================================
// APPROVAL WORKFLOW
// ============================================================================

/**
 * Approve a post for publishing
 */
export async function approvePost(postId: string, notes?: string): Promise<void> {
  await supabase
    .from('marketing_blog_posts')
    .update({
      status: 'approved',
      approval_notes: notes,
      updated_at: new Date().toISOString()
    })
    .eq('id', postId);
}

/**
 * Reject a post - deletes it from the database
 */
export async function rejectPost(postId: string, reason: string): Promise<void> {
  // Delete the post completely when rejected
  await supabase
    .from('marketing_blog_posts')
    .delete()
    .eq('id', postId);
  
  console.log(`Post ${postId} rejected and deleted. Reason: ${reason}`);
}

/**
 * Publish approved posts
 */
export async function publishScheduledPosts(): Promise<void> {
  const now = new Date().toISOString();
  
  const { data: posts } = await supabase
    .from('marketing_blog_posts')
    .select('*')
    .eq('status', 'scheduled')
    .lte('scheduled_for', now);

  if (!posts || posts.length === 0) return;

  for (const post of posts) {
    try {
      const siteUrl = `https://${post.target_site}.com`;
      const result = await postToWordPress(post, siteUrl);

      await supabase
        .from('marketing_blog_posts')
        .update({
          status: 'published',
          published_at: new Date().toISOString(),
          wordpress_post_id: result.post_id,
          wordpress_url: result.url
        })
        .eq('id', post.id);

      console.log(`Published post: ${post.title} to ${siteUrl}`);
    } catch (error) {
      console.error(`Failed to publish post ${post.id}:`, error);
      
      await supabase
        .from('marketing_blog_posts')
        .update({
          status: 'failed',
          error_message: error instanceof Error ? error.message : 'Unknown error'
        })
        .eq('id', post.id);
    }
  }
}
